<script setup lang="ts">
import type { SiteSectionsTreeSectionCategoryFilterOption } from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-filter.types";
import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { V4HSiteId, SiteSectionCategoryId } from "@/data/common/ids";
import type { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import type { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import type { IVirtualizedTreeNode, IVirtualizedTreeNodeModel } from "@voltalis/ui-kit/components";

import { cn } from "@voltalis/frontend-utils";
import { SectionAppliancesCountByProgrammingCategory, SiteTreeStructure } from "@voltalis/ui-kit/components";
import { CubeIcon } from "@voltalis/ui-kit/icons";
import { MarkerPinIcon } from "@voltalis/ui-kit/icons";
import { isEmptyObject } from "es-toolkit";

import {
  isPendingSiteSectionPreviewNode,
  type TPendingSiteSectionPreviewNode,
} from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree-pending-preview";
import { canDropSiteSectionNode } from "@/app/tools/new-section-management/site-sections-tree/tree-display/section-tree-drop-rules";
import TagIcon from "@/assets/icons/tag-02.svg?component";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";
import { countAllLeafDescendants } from "@/utils/site-section/site-section-utils";

import Skeleton from "primevue/skeleton";

import SiteSectionsTreeSectionCategorySelector from "@/app/tools/new-section-management/site-sections-tree/filters/section-category-filter/site-sections-tree-section-category-selector.vue";
import SiteSectionsTreeDisplayHeader from "@/app/tools/new-section-management/site-sections-tree/tree-display/site-sections-tree-display-header.vue";

type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;
type TreeNodeModel = IVirtualizedTreeNodeModel<TSiteSectionsTreeData>;

const props = defineProps<{
  assignSectionCategory: (
    section: NonNullable<TSiteSectionsTreeData>,
    sectionCategoryId: SiteSectionCategoryId,
  ) => Promise<boolean>;
  applianceFilterSiteSectionsTree?: TreeNode;
  canDragAndDropSiteSections: boolean;
  categoryOptions: readonly SiteSectionsTreeSectionCategoryFilterOption[];
  createCategory: (name: string) => Promise<boolean>;
  hideRootNode: boolean;
  isCreatingCategory: boolean;
  isLoading: boolean;
  isTreeMutationPending: boolean;
  siteId: V4HSiteId;
  siteSectionsTree?: TreeNode;
  visibleSectionsCountLabel: string;
}>();

const searchQuery = defineModel<string>("searchQuery", { required: true });
const selectedApplianceCategories = defineModel<EProgramApplianceCategory[]>("selectedApplianceCategories", {
  required: true,
});
const selectedSectionCategories = defineModel<SiteSectionCategoryId[]>("selectedSectionCategories", { required: true });

const emit = defineEmits<{
  (event: "dropped", draggedNode: TreeNodeModel, targetDropNode: TreeNodeModel): void;
  (event: "enter-bulk-category-edition"): void;
  (event: "open-create-section-drawer", section?: TreeNode): void;
  (event: "remove-appliance-category", category: EProgramApplianceCategory): void;
  (event: "remove-section-category", categoryId: SiteSectionCategoryId): void;
  (event: "view-section", section: SectionNode): void;
}>();

const { t, k } = useTypedIntl();

function assignSectionCategory(section: TSiteSectionsTreeData, sectionCategoryId: SiteSectionCategoryId) {
  if (!section) return Promise.resolve(false);

  return props.assignSectionCategory(section, sectionCategoryId);
}

function canDropSiteSectionNodeWithPendingPreview(draggedNode: TreeNodeModel, targetDropNode: TreeNodeModel): boolean {
  if (isPendingSiteSectionPreviewNode(draggedNode) || isPendingSiteSectionPreviewNode(targetDropNode)) return false;

  return canDropSiteSectionNode(draggedNode, targetDropNode);
}
</script>

<template>
  <SiteTreeStructure
    :auto-scroll-to-node="isPendingSiteSectionPreviewNode"
    :class="cn('transition-opacity', { 'opacity-70': props.isTreeMutationPending })"
    :is-loading="props.isLoading"
    :siteSectionsTree="props.siteSectionsTree"
    :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteStructure"
    :draggable="props.canDragAndDropSiteSections"
    :hideRootNode="props.hideRootNode"
    :canDropNode="canDropSiteSectionNodeWithPendingPreview"
    @dropped="(draggedNode, targetDropNode) => emit('dropped', draggedNode, targetDropNode)"
  >
    <template #header>
      <SiteSectionsTreeDisplayHeader
        v-model:search-query="searchQuery"
        v-model:selected-appliance-categories="selectedApplianceCategories"
        v-model:selected-section-categories="selectedSectionCategories"
        :category-options="props.categoryOptions"
        :create-category="props.createCategory"
        :is-creating-category="props.isCreatingCategory"
        :site-sections-tree="props.applianceFilterSiteSectionsTree"
        :visible-sections-count-label="props.visibleSectionsCountLabel"
        show-bulk-edition-trigger
        @enter-bulk-category-edition="emit('enter-bulk-category-edition')"
        @remove-appliance-category="emit('remove-appliance-category', $event)"
        @remove-section-category="emit('remove-section-category', $event)"
      />
    </template>

    <template #loader>
      <Skeleton
        class="!h-full"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.skeleton"
      />
    </template>

    <template #nodeIcon="{ section }">
      <CubeIcon
        v-if="isPendingSiteSectionPreviewNode(section)"
        class="shrink-0 text-gray-300"
      />
      <MarkerPinIcon
        v-else-if="section.data === null"
        class="shrink-0"
      />
      <CubeIcon
        v-else
        class="shrink-0"
      />
    </template>

    <template #sectionName="{ section }">
      <div
        v-if="section.data === null"
        class="flex items-center w-full h-full justify-between group"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteCard.card"
        :data-site-id="props.siteId"
      >
        <span
          class="font-medium text-base text-gray-700 overflow-hidden whitespace-nowrap text-ellipsis"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteCard.siteName"
        >
          {{ section.name }}
        </span>

        <div class="actions-buttons pr-2 flex shrink-0 group-hover:opacity-100 opacity-0">
          <button
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.siteCard.siteCardAddButton"
            @click.stop="emit('open-create-section-drawer', section)"
          >
            <i class="pi pi-plus" />
          </button>
        </div>
      </div>
      <div
        v-else-if="isPendingSiteSectionPreviewNode(section)"
        class="pending-site-section-preview flex items-center w-full h-full justify-between"
        data-pending-site-section-preview="true"
        :data-pending-site-section-preview-type="
          (section as TPendingSiteSectionPreviewNode).state.pendingSiteSectionPreviewType
        "
      >
        <span
          class="font-medium text-base text-gray-300 overflow-hidden whitespace-nowrap text-ellipsis"
          :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionName"
        >
          {{ section.name }}
        </span>
      </div>
      <div
        v-else
        class="flex items-center w-full h-full justify-between group cursor-pointer"
        :data-section-id="section.data?.id"
        :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.card"
        @click="emit('view-section', section.data)"
      >
        <div class="flex flex-col min-w-0">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="font-medium text-base text-gray-700 overflow-hidden whitespace-nowrap text-ellipsis"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionName"
            >
              {{ section.name }}
            </span>

            <span
              v-if="section.parentsIndexes.length && countAllLeafDescendants(section)"
              class="text-gray-400 font-semibold"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionChildrenCount"
            >
              ({{ countAllLeafDescendants(section) }})
            </span>

            <span
              v-if="section.data.category"
              class="flex shrink-0 items-center justify-center gap-0.5 rounded-xl bg-gray-100 px-2 py-1 text-xs font-semibold not-italic leading-normal text-gray-500"
              :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionCategory"
            >
              {{ section.data.category.name }}
            </span>
          </div>
          <SectionAppliancesCountByProgrammingCategory
            v-if="section.data && !isEmptyObject(section.data.applianceCountByCategory)"
            :applianceCountByProgrammingCategory="section.data.applianceCountByCategory"
          />
        </div>
        <div class="actions-buttons pr-2 flex shrink-0 group-hover:opacity-100 opacity-0 gap-3">
          <button
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionCardAddButton"
            @click.stop="emit('open-create-section-drawer', section)"
          >
            <i class="pi pi-plus font-bold" />
          </button>

          <SiteSectionsTreeSectionCategorySelector
            mode="single"
            :selected-categories="section.data.category ? [section.data.category.id] : []"
            :options="props.categoryOptions"
            :create-category="props.createCategory"
            :is-creating-category="props.isCreatingCategory"
            :select-category="(sectionCategoryId) => assignSectionCategory(section.data, sectionCategoryId)"
          >
            <template #trigger="{ toggle }">
              <button
                type="button"
                :aria-label="t(k.tools.siteManagement.siteTreeStructure.categoryAssignment.action)"
                :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionCardCategoryButton"
                @click.stop="toggle"
              >
                <TagIcon class="size-4" />
              </button>
            </template>
          </SiteSectionsTreeSectionCategorySelector>

          <button
            :data-test-id="DATA_TEST_IDS.views.siteSectionTree.sectionCard.sectionCardEditButton"
            @click="emit('view-section', section.data)"
          >
            <i class="pi pi-pencil" />
          </button>
        </div>
      </div>
    </template>
  </SiteTreeStructure>
</template>

<style lang="scss">
.actions-buttons button {
  @apply p-1 rounded-lg w-[34px] h-[34px] flex items-center justify-center;

  &:hover {
    @apply bg-blue-100/30;

    > i,
    > svg {
      @apply text-brand;
    }
  }
}
</style>
